import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../cart';
import { useMoney } from '../currency';
import { usePulse } from '../motion';
import { FreeShippingBar } from './FreeShippingBar';

/**
 * Košík v hlavičce s vysouvacím obsahem.
 *
 * Otevírá se sám, jakmile do košíku něco přibude – zákazník tak vidí
 * potvrzení nákupu na místě, kam se dívá, a nemusí kvůli tomu opouštět
 * stránku se zbožím. Po chvíli se zase zavře, aby nepřekážel.
 *
 * Kromě toho jde otevřít najetím myší i z klávesnice; zavírá ho Escape,
 * odchod fokusu i kliknutí mimo. Samotný odkaz vede pořád na /kosik,
 * takže košík funguje i bez JavaScriptu a na dotykovém displeji.
 */

/** Jak dlouho zůstane otevřený po přidání zboží. */
const AUTO_CLOSE_MS = 4_000;
/** Prodleva při odjetí myší – bez ní se panel zavře cestou k němu. */
const HOVER_CLOSE_MS = 250;

export function CartMenu() {
  const cart = useCart();
  const { format, isConverted } = useMoney();
  const pulsing = usePulse(cart.count);
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement | null>(null);
  const closeTimer = useRef<number | undefined>(undefined);
  const previousCount = useRef(cart.count);

  function cancelClose() {
    if (closeTimer.current !== undefined) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = undefined;
    }
  }

  function scheduleClose(delay: number) {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), delay);
  }

  // Přibylo zboží → otevřít. Ubylo (mazání z panelu) → nechat být, jinak
  // by panel po vyprázdnění košíku znovu vyskočil.
  useEffect(() => {
    const grew = cart.count > previousCount.current;
    previousCount.current = cart.count;
    if (!grew) return undefined;

    setOpen(true);
    scheduleClose(AUTO_CLOSE_MS);
    return undefined;
  }, [cart.count]);

  useEffect(() => cancelClose, []);

  // Prázdný košík nemá co ukazovat – panel by byl prázdné okno.
  useEffect(() => {
    if (cart.count === 0) setOpen(false);
  }, [cart.count]);

  useEffect(() => {
    if (!open) return undefined;

    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    function onPointer(event: MouseEvent) {
      if (wrap.current !== null && !wrap.current.contains(event.target as Node)) setOpen(false);
    }

    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open]);

  const empty = cart.count === 0;

  return (
    <div
      className="cart-menu"
      ref={wrap}
      onMouseEnter={() => {
        cancelClose();
        if (!empty) setOpen(true);
      }}
      onMouseLeave={() => scheduleClose(HOVER_CLOSE_MS)}
      onFocus={() => {
        cancelClose();
        if (!empty) setOpen(true);
      }}
      onBlur={(event) => {
        // Fokus uvnitř panelu ho zavřít nesmí.
        if (!event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false);
      }}
    >
      <Link to="/kosik" className="cart-link" aria-expanded={open} aria-haspopup="true">
        <CartIcon />
        <span className="cart-label">Košík</span>
        {!empty && (
          <span className="cart-total">{format(cart.estimatedTotalMinor)}</span>
        )}
        {cart.count > 0 && (
          <span className={pulsing ? 'cart-count is-pulsing' : 'cart-count'}>{cart.count}</span>
        )}
      </Link>

      {open && !empty && (
        <div className="cart-panel" role="dialog" aria-label="Obsah košíku">
          <ul className="cart-lines">
            {cart.lines.map((line) => (
              <li key={line.variantId}>
                <Link to={`/produkt/${line.slug}`} className="cart-line-name">
                  <strong>{line.productName}</strong>
                  {line.variantName !== '' && (
                    <span className="muted small">{line.variantName}</span>
                  )}
                </Link>
                <span className="cart-line-qty muted small">{line.quantity}×</span>
                <span className="cart-line-price">
                  {format(line.priceMinor * line.quantity)}
                </span>
                <button
                  type="button"
                  className="cart-line-remove"
                  aria-label={`Odebrat ${line.productName}`}
                  onClick={() => cart.remove(line.variantId)}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>

          <div className="cart-panel-foot">
            <span className="muted small">Zboží celkem</span>
            <strong>{format(cart.estimatedTotalMinor)}</strong>
          </div>

          <FreeShippingBar subtotalMinor={cart.estimatedTotalMinor} />

          {/*
            Částka je orientační – závazný součet i s dopravou počítá až
            server v pokladně. Slibovat tady konečnou cenu by znamenalo
            slíbit číslo, které se u pokladny může změnit.
          */}
          <p className="muted small cart-panel-note">
            Doprava se dopočítá v pokladně.
            {isConverted && ' Ceny jsou orientační, platba probíhá v Kč.'}
          </p>

          <div className="cart-panel-actions">
            <Link to="/kosik" className="button ghost">
              Do košíku
            </Link>
            <Link to="/pokladna" className="button primary">
              K pokladně
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function CartIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        d="M4 5h2l2.2 9.4a2 2 0 0 0 2 1.6h6.1a2 2 0 0 0 2-1.5L20 8H7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="10" cy="19" r="1.4" fill="currentColor" />
      <circle cx="17" cy="19" r="1.4" fill="currentColor" />
    </svg>
  );
}
